import { call, put, select, takeLatest } from 'redux-saga/effects';
import * as apollo from 'apollo/leads.query';
import { fetchOrgMembers } from 'apollo/organization.query';
import { fetchLeadAssignments } from 'api/leads';
import {
  GET_LEADS_STATUSES,
  GET_LEADS_STATUSES_SUCCESS,
  GET_DEFAULT_ASSIGNMENTS,
  GET_INCOMING_NOTIFICATION_USERS,
} from 'containers/Settings/Leads/types';
import { objToCamelCase } from 'utils/general';
import * as types from './types';

function* getOrganizationMembers(action) {
  try {
    const result = yield call(fetchOrgMembers, action.payload);
    const response = result?.data?.organizationMembers || {};
    yield put({ type: types.GET_ORG_MEMBERS_SUCCESS, payload: response });

    const permittedMembers = response
      ?.filter(m => m.permissions['lead.view'])
      .sort((m1, m2) => m1.firstname.localeCompare(m2.firstname));

    yield put({ type: GET_DEFAULT_ASSIGNMENTS, payload: permittedMembers });
    yield put({ type: GET_INCOMING_NOTIFICATION_USERS, payload: permittedMembers });
  } catch (err) {
    yield put({ type: types.GET_ORG_MEMBERS_FAIL });
  }
}

function* getSingleLead(action) {
  try {
    const result = yield call(apollo.fetchLead, action.payload);
    const response = result?.data.lead || {};
    yield put({ type: types.GET_SINGLE_LEAD_SUCCESS, payload: response });
  } catch (err) {
    yield put({ type: types.GET_SINGLE_LEAD_FAIL });
  }
};

function* getLeadsByStatus(action) {
  try {
    const result = yield call(apollo.fetchLeadsByStatus, action.payload);
    const response = result?.data?.leadsByStatus || {};
    yield put({ type: types.GET_LEADS_SUCCESS, payload: response });
  } catch (err) {
    yield put({ type: types.GET_LEADS_FAIL });
  }
}

function* searchLeads(action) {
  try {
    const result = yield call(apollo.searchLeads, action.payload);
    yield put({ type: types.SEARCH_LEADS_SUCCESS, payload: result?.data?.leadSearch });
  } catch (err) {
    yield put({ type: types.SEARCH_LEADS_FAIL });
  }
}

function* getFilters(action) {
  try {
    const result = yield call(apollo.fetchCustomFilters, action.payload);
    yield put({ type: types.GET_LEADS_FILTERS_SUCCESS, payload: result?.data?.getLeadCustomFilters });
  } catch (err) {
    yield put({ type: types.GET_LEADS_FILTERS_FAIL });
  }
}

function* getStatuses() {
  const statuses = yield select(state => state.leads.filters);
  try {
    // Fetch filters if not already fetched. Needed for certain data.
    if (statuses.length < 1) {
      const result = yield call(apollo.fetchCustomFilters);
      yield put({ type: types.GET_LEADS_FILTERS_SUCCESS, payload: result?.data?.getLeadCustomFilters });
    }
    const results = yield call(apollo.fetchCustomStatuses);
    const payload = results?.data?.getLeadCustomStatuses;
    yield put({ type: GET_LEADS_STATUSES_SUCCESS, payload });
  }  catch (err) {
    yield put({ type: types.GET_LEADS_FILTERS_FAIL });
  }
}

function* getFiltersInBackground(action) {
  try {
    const result = yield call(apollo.fetchCustomFilters, action.payload);
    yield put({ type: types.GET_LEADS_FILTERS_SUCCESS, payload: result?.data?.getLeadCustomFilters });
  } catch (err) {
    yield put({ type: types.GET_LEADS_FILTERS_FAIL });
  }
}

function* updateLeadStatus(action) {
  const { variables, current } = action.payload;
  try {
    const { data } = yield call(apollo.updateLeadStatus, variables);
    if (data && data?.updateLead?.lead) {
      const leadId = data?.updateLead?.lead?.id;
      const next = data?.updateLead?.lead?.leadCustomStatusId;
      const prev = current;
      if (next === prev) return;
      // client.clearStore();
      yield put({ type: types.UPDATE_LEAD_SUCCESS, payload: { leadId, next, prev } });
    } else {
      yield put({ type: types.UPDATE_LEAD_ERROR, payload: current });
    }
  } catch (err) {
    yield put({ type: types.UPDATE_LEAD_ERROR, payload: current });
  }
}

function* updateLeadLastContacted(action) {
  const { variables } = action.payload;
  try {
    const { data } = yield call(apollo.updateLeadLastContacted, variables);
    if (data && data?.updateLead?.lead) {
      const leadId = data?.updateLead?.lead?.id;
      const lastContacted = data?.updateLead?.lead?.lastContactedDate;
      yield put({ type: types.UPDATE_LEAD_SUCCESS, payload: { leadId, lastContacted } });
    } else {
      yield put({ type: types.UPDATE_LEAD_ERROR });
    }
  } catch (err) {
    yield put({ type: types.UPDATE_LEAD_ERROR });
  }
}

function* deleteLeadSaga(action) {
  const { variables } = action.payload;
  const { id } = variables;
  try {
    const { data } = yield call(apollo.deleteLead, variables);
    const isDeleted = data?.deleteLead?.isDeleted;
    if (data && isDeleted) {
      yield put({ type: types.DELETE_LEAD_SUCCESS, payload: { deleteId: id } });
    } else {
      yield put({ type: types.DELETE_LEAD_ERROR, payload: 'deleteLeadFailed' });
    }
  } catch (err) {
    yield put({ type: types.DELETE_LEAD_ERROR, payload: 'deleteLeadFailed' });
  }
}

function* createLeadAssignment(action) {
  try {
    const { variables } = action.payload;
    const { data } = yield call(apollo.createLeadAssignment, variables);

    if (data && data?.orgMemberLeadAssignmentCreate?.orgMemberLeadAssignment?.leadId) {
      const leadId = data?.orgMemberLeadAssignmentCreate?.orgMemberLeadAssignment?.leadId;
      const loginId = data?.orgMemberLeadAssignmentCreate?.orgMemberLeadAssignment?.loginId;
      const name = data?.orgMemberLeadAssignmentCreate?.orgMemberLeadAssignment?.name;
      yield put({ type: types.CREATE_LEAD_ASSIGNMENT_SUCCESS, payload: { leadId, loginId, name } });
    } else {
      yield put({ type: types.CREATE_LEAD_ASSIGNMENT_FAIL });
    }
  } catch (err) {
    yield put({ type: types.CREATE_LEAD_ASSIGNMENT_FAIL });
  }
}

function* deleteLeadAssignment(action) {
  try {
    const { variables } = action.payload;
    const { loginId, leadId } = variables.input;
    const { data } = yield call(apollo.deleteLeadAssignment, variables);
    const isDeleted = data?.orgMemberLeadAssignmentDelete?.isDeleted;

    if (data && isDeleted) {
      yield put({ type: types.DELETE_LEAD_ASSIGNMENT_SUCCESS, payload: { leadId, loginId } });
    } else {
      yield put({ type: types.DELETE_LEAD_ASSIGNMENT_FAIL });
    }
  } catch (err) {
    yield put({ type: types.DELETE_LEAD_ASSIGNMENT_FAIL });
  }
}

function* getLeadAssignments(action) {
  try {
    const { orgId, leadId } = action.payload;

    // Doesn't seem like we need to notify the user if this fetch fails
    // as it just helps to populate the lead assignment dropdown in various
    // contexts.
    if (!orgId || !leadId) {
      return;
    }
    const { data, ok } = yield call(fetchLeadAssignments, orgId, leadId);
    if (data && ok) {
      yield put({
        type: types.UPDATE_LEAD_ASSIGNMENTS_STATE,
        payload: { leadId, data: data.map(objToCamelCase) },
      });
    }
  } catch {
    // pass through.
  }
}

export default function* watchList() {
  yield takeLatest(types.GET_ORG_MEMBERS, getOrganizationMembers);
  yield takeLatest(types.GET_SINGLE_LEAD, getSingleLead);
  yield takeLatest(types.GET_LEADS, getLeadsByStatus);
  yield takeLatest(types.SEARCH_LEADS, searchLeads);
  yield takeLatest(types.GET_LEADS_FILTERS, getFilters);
  yield takeLatest(GET_LEADS_STATUSES, getStatuses);
  yield takeLatest(types.GET_LEADS_FILTERS_IN_BACKGROUND, getFiltersInBackground);
  yield takeLatest(types.UPDATE_LEAD_STATUS, updateLeadStatus);
  yield takeLatest(types.UPDATE_LEAD_LAST_CONTACTED, updateLeadLastContacted);
  yield takeLatest(types.DELETE_LEAD_STATUS, deleteLeadSaga);
  yield takeLatest(types.CREATE_LEAD_ASSIGNMENT, createLeadAssignment);
  yield takeLatest(types.DELETE_LEAD_ASSIGNMENT, deleteLeadAssignment);
  yield takeLatest(types.GET_LEAD_ASSIGNMENTS, getLeadAssignments);
}
